import {
  addDaysToDate,
  onErrorMessage,
  onSuccessMessage,
  useValidationErrors,
} from '@/helpers'
import {
  AddressProps,
  CatalogProductProps,
  SupplierForecastProps,
  SupplierWarehouseProps,
} from '@/types'
import { useEffect, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useTranslation } from 'react-i18next'

import { Button, DrawerStepperProgress } from '@mercai/clever'

import {
  QUERIES_ADMIN,
  useCatalogProductDetail,
  useCreateSupplierForecastWithSupplierProduct,
  useCreateSupplierWarehouse,
  useForecastDetail,
  useSupplierDetail,
  useUpdateSupplierForecastWithSupplierProduct,
} from '@/services'

import { SupplierForecastDrawerFormProps } from './supplier-forecast-drawer-form.type'

import {
  schemaSupplierForecast,
  SelectCatalogProduct,
  SupplierForecastForm,
} from '@/components'

import { AddressForm, addressSchema } from '../forms/address-form'

export const SupplierForecastDrawerForm = ({
  isOpen,
  onHandleOpen,
  catalogProductUuid,
  onCompleteCreate,
  supplierForecastUuid,
  supplierUuid,
}: SupplierForecastDrawerFormProps) => {
  const { t } = useTranslation('componentsSupplierForecastDrawerForm')

  const queryClient = useQueryClient()

  const isNewSupplierProduct = !supplierForecastUuid

  const [values, setValues] = useState<SupplierForecastProps>({
    supplierProduct: {
      catalogProductUuid,
    },
  } as SupplierForecastProps)

  const { data: supplierForecastData } = useForecastDetail(supplierForecastUuid)

  const { data } = useCatalogProductDetail(
    !isOpen ? undefined : catalogProductUuid,
  )

  const { data: supplierData } = useSupplierDetail(
    !isOpen ? undefined : supplierUuid,
  )

  const { isValid, errors, onHandleListenErrors } = useValidationErrors(
    schemaSupplierForecast(t),
    values,
  )

  const [newWarehouseValues, setNewWarehouseValues] =
    useState<Partial<AddressProps>>()

  const {
    isValid: isValidWarehouse,
    errors: errorsWarehouse,
    onHandleListenErrors: onHandleListenErrorsWarehouse,
  } = useValidationErrors(addressSchema(t), newWarehouseValues)

  const { mutateAsync: mutateAsyncCreateSupplierForecastWithSupplierProduct } =
    useCreateSupplierForecastWithSupplierProduct()

  const { mutateAsync: mutateAsyncUpdateSupplierForecastWithSupplierProduct } =
    useUpdateSupplierForecastWithSupplierProduct()

  const { mutateAsync: mutateAsyncCreateSupplierWarehouse } =
    useCreateSupplierWarehouse()

  const onSuccessUpdatedNewWarehouse = () => {
    queryClient.invalidateQueries({
      queryKey: [QUERIES_ADMIN.SUPPLIER_WAREHOUSE.module],
    })
  }

  const onHandleSubmit = async () => {
    if (!isValid) {
      onHandleListenErrors(true)
      return
    }

    onHandleListenErrors(false)

    let response: {
      success: boolean
      data?: SupplierForecastProps
    } = {
      success: false,
      data: undefined,
    }

    const payload: Partial<SupplierForecastProps> = {
      ...values,
      currency: values?.currency || 'USD',
      cost: +(values?.cost || 0),
      availableQuantity: undefined,
      createdAt: undefined,
      updatedAt: undefined,
      reference: undefined,
      quantityUsed: undefined,
      productQuotations: undefined,
      expiresAt: values?.expiresAt ? values?.expiresAt : undefined,
      supplierProduct: {
        ...values?.supplierProduct,
        catalogProductUuid:
          values?.supplierProduct?.catalogProductUuid || data?.data?.uuid,
        supplierUuid:
          values?.supplierProduct?.supplierUuid || supplierData?.data?.uuid,
        catalogProduct: undefined,
        createdAt: undefined,
        updatedAt: undefined,
        category: undefined,
        reference: undefined,
        supplierWarehouse: undefined,
        supplier: undefined,
        totalQuantityUsed: undefined,
        saleOrderItems: undefined,
      },
    }

    if (isNewSupplierProduct) {
      response = await mutateAsyncCreateSupplierForecastWithSupplierProduct({
        ...payload,
        uuid: undefined,
      })
    } else {
      response =
        await mutateAsyncUpdateSupplierForecastWithSupplierProduct(payload)
    }

    if (response.success) {
      onSuccessMessage(
        isNewSupplierProduct
          ? t('supplierForecastDrawerFormSuccessCreateTitle')
          : t('supplierForecastDrawerFormSuccessEditTitle'),
        isNewSupplierProduct
          ? t('supplierForecastDrawerFormSuccessCreateMessage')
          : t('supplierForecastDrawerFormSuccessEditMessage'),
      )

      onHandleOpen()

      onCompleteCreate?.()
    } else {
      onErrorMessage(
        isNewSupplierProduct
          ? t('supplierForecastDrawerFormErrorCreateTitle')
          : t('supplierForecastDrawerFormErrorEditTitle'),
        isNewSupplierProduct
          ? t('supplierForecastDrawerFormErrorCreateMessage')
          : t('supplierForecastDrawerFormErrorEditMessage'),
      )
    }
  }

  const onHandleSubmitNewWarehouse = async (
    onNavigateToPreviousStep: () => void,
  ) => {
    if (!isValidWarehouse) {
      onHandleListenErrorsWarehouse(true)
      return
    }

    onHandleListenErrorsWarehouse(false)

    const response = await mutateAsyncCreateSupplierWarehouse({
      ...newWarehouseValues,
      supplierUuid: values?.supplierProduct?.supplier?.uuid,
    } as Partial<SupplierWarehouseProps>)

    if (response.success) {
      onNavigateToPreviousStep()

      onSuccessUpdatedNewWarehouse()
    } else {
      onErrorMessage(
        t('supplierForecastDrawerFormWarehouseErrorCreateTitle'),
        t('supplierForecastDrawerFormWarehouseErrorCreateMessage'),
      )
    }
  }

  const openNewWarehouse = (
    onNavigateToNextStep: (nextRoute: string) => void,
  ) => {
    onNavigateToNextStep('warehouse')
  }

  const onClose = () => {
    onHandleOpen?.()
    setValues({} as SupplierForecastProps)
    setNewWarehouseValues({})
  }

  const title = isNewSupplierProduct
    ? t('supplierForecastDrawerFormTitle')
    : t('supplierForecastDrawerFormEditTitle')

  const description = isNewSupplierProduct
    ? t('supplierForecastDrawerFormDescription')
    : t('supplierForecastDrawerFormEditDescription')

  useEffect(() => {
    if (isOpen && isNewSupplierProduct) {
      setValues(
        (prev) =>
          ({
            ...prev,
            expiresAt:
              prev?.expiresAt || addDaysToDate(30).toISOString().split('T')[0],
            supplierProduct: {
              ...prev.supplierProduct,
              catalogProductUuid,
            },
          }) as SupplierForecastProps,
      )
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      setValues(
        (prev) =>
          ({
            ...prev,
            ...supplierForecastData?.data,
            supplierProduct: {
              ...supplierForecastData?.data?.supplierProduct,
              ...(supplierForecastData?.data?.supplierProduct?.catalogProduct
                ?.uuid
                ? {
                    catalogProductUuid:
                      supplierForecastData?.data?.supplierProduct
                        ?.catalogProduct?.uuid,
                  }
                : {}),
              ...(supplierForecastData?.data?.supplierProduct?.supplier?.uuid
                ? {
                    supplierUuid:
                      supplierForecastData?.data?.supplierProduct?.supplier
                        ?.uuid,
                  }
                : {}),
            },
          }) as SupplierForecastProps,
      )
    }
  }, [supplierForecastData?.data, isOpen])

  useEffect(() => {
    if (data?.data && isOpen) {
      const catalogProduct = data?.data as CatalogProductProps
      setValues((prev) => ({
        ...prev,
        supplierProduct: {
          ...prev.supplierProduct,
          catalogProductUuid: catalogProduct?.uuid,
          catalogProduct,
        },
      }))
    }
  }, [data?.data, isOpen])

  useEffect(() => {
    if (supplierData?.data && isOpen) {
      setValues((prev) => ({
        ...prev,
        supplierProduct: {
          ...prev.supplierProduct,
          supplierUuid: supplierData?.data?.uuid,
          supplier: supplierData?.data,
        },
      }))
    }
  }, [supplierData?.data, isOpen])

  return (
    <DrawerStepperProgress
      isOpen={isOpen}
      onHandleOpen={onClose}
      initialStep={!catalogProductUuid ? 'root' : 'supplier_forecast'}
      translations={{ optional: '' }}
      title={title}
      description={description}
      totalSteps={catalogProductUuid ? 1 : 2}
      steps={{
        root: {
          stepNumber: 0,
          isOptional: false,
          backNavigate: '',
          component: () => (
            <SelectCatalogProduct
              errors={errors}
              onSelectProductItem={(productItem: CatalogProductProps) => {
                setValues((prev) => ({
                  ...prev,
                  supplierProduct: {
                    ...prev.supplierProduct,
                    catalogProduct: productItem,
                    catalogProductUuid: productItem.uuid,
                  },
                }))
              }}
              productsItem={
                values?.supplierProduct?.catalogProduct
                  ? [values?.supplierProduct?.catalogProduct]
                  : []
              }
            />
          ),
          footer: ({ onNavigateToNextStep }) => (
            <>
              <Button variant="secondary" onClick={onClose}>
                {t('drawerFormBack')}
              </Button>
              <Button onClick={() => onNavigateToNextStep('supplier_forecast')}>
                {t('drawerFormSubmit')}
              </Button>
            </>
          ),
        },
        supplier_forecast: {
          stepNumber: 2,
          isOptional: false,
          backNavigate: !catalogProductUuid ? 'root' : '',
          component: ({ onNavigateToNextStep }) => (
            <SupplierForecastForm
              errors={errors}
              onChangeValues={(values) =>
                setValues((prev) => ({ ...prev, ...values }))
              }
              values={values}
              productItem={data?.data as CatalogProductProps}
              openNewWarehouse={() => openNewWarehouse(onNavigateToNextStep)}
            />
          ),
          footer: ({ onNavigateToPreviousStep }) => (
            <>
              <Button
                variant="secondary"
                onClick={() =>
                  !catalogProductUuid ? onNavigateToPreviousStep() : onClose()
                }
              >
                {t('drawerFormBack')}
              </Button>
              <Button onClick={onHandleSubmit}>{t('drawerFormSubmit')}</Button>
            </>
          ),
        },
        warehouse: {
          stepNumber: 2,
          isOptional: false,
          backNavigate: 'supplier_forecast',
          component: () => (
            <AddressForm
              errors={errorsWarehouse}
              onChangeValues={setNewWarehouseValues}
              values={newWarehouseValues}
              isWarehouse
            />
          ),
          footer: ({ onNavigateToPreviousStep }) => (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  onNavigateToPreviousStep()
                  setNewWarehouseValues({})
                }}
              >
                {t('drawerFormBack')}
              </Button>
              <Button
                onClick={() =>
                  onHandleSubmitNewWarehouse(onNavigateToPreviousStep)
                }
              >
                {t('drawerFormSubmit')}
              </Button>
            </>
          ),
        },
      }}
    />
  )
}
